/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'
import { useServiceContext } from '../../root'
import { TaskViewType, TasksApiFilter } from './types'
import { assembleFiltersFromFormValues } from './utils'
import TasksTable from './TasksTable'
import TasksFilter from './TasksFilter'
import { RcmTableDataWrapper } from '../../components/rcm-table/RcmTableDataWrapper'
import RcmTableFilterTrigger from '../../components/rcm-table/RcmTableFilterTrigger'

/**
 * Tasks Screen Component
 *
 * Main tasks page that displays tasks based on the selected view type:
 * - me: Tasks assigned to current user
 * - all: All tasks
 * - completed: Completed tasks
 * - all-opened: All open tasks
 */
const Tasks: FC = () => {
  const { tasksTab = 'me' } = useParams<{ tasksTab: string }>()
  const { t } = useTranslation()
  const { service, api } = useServiceContext()

  const viewType = (tasksTab as TaskViewType) || TaskViewType.ME
  const auth = service.auth
  const isFilterShown = service.access.isAllowed('tasksFilters')
  const currentUser = auth.getUser()

  // Filter state - Initialize with logged-in user for ME view
  const [filterValues, setFilterValues] = useState<any>({})
  const [isInitialized, setIsInitialized] = useState(false)
  const refreshTableRef = useRef<(() => void) | null>(null)

  // Listen for 'updateTasks' event to refresh table
  // Matches legacy: $rootScope.$on('updateTasks', ...)
  useEffect(() => {
    const handleUpdateTasks = () => {
      if (refreshTableRef.current) {
        refreshTableRef.current()
      }
    }

    window.addEventListener('updateTasks', handleUpdateTasks as EventListener)

    return () => {
      window.removeEventListener('updateTasks', handleUpdateTasks as EventListener)
    }
  }, [])

  // Initialize filter with logged-in user ONLY for ME view
  useEffect(() => {
    if (currentUser && !isInitialized) {
      if (viewType === TaskViewType.ME) {
        // For ME view, ALWAYS default to current user (matches old implementation)
        setFilterValues({ userId: [currentUser.id] })
      } else {
        // For other views (ALL, COMPLETED, ALL_OPENED), reset to empty object
        setFilterValues({})
      }
      setIsInitialized(true)
    }
  }, [currentUser, viewType, isFilterShown, isInitialized])

  // Reset filter when view type changes
  useEffect(() => {
    setIsInitialized(false) // Trigger re-initialization
    setFilterValues({}) // Clear filter values immediately
  }, [viewType])

  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(
    async (pagingParams: any, filter: any) => {
      // Use filter parameter passed from RcmTableDataWrapper (when Filter button is clicked)
      // Fall back to filterValues state for initial load
      const activeFilter = filter || filterValues

      // Build base parameters with pagination and sorting
      const params: any = {
        pageSize: pagingParams.pageSize || 15,
        pageStart: pagingParams.pageStart || 0,
        sortBy: pagingParams.sortBy || 'START_DATE',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : true
      }

      // Add task status based on view type
      switch (viewType) {
        case TaskViewType.COMPLETED:
          params.taskStatus = ['REJECTED', 'APPROVED', 'CANCELLED', 'SUSPENDED']
          break
        case TaskViewType.ALL:
          params.taskStatus = ['OPEN', 'REJECTED', 'APPROVED', 'CANCELLED', 'SUSPENDED']
          break
        case TaskViewType.ALL_OPENED:
          params.taskStatus = 'OPEN'
          break
        case TaskViewType.ME:
          params.taskStatus = 'OPEN'
          break
      }

      // Add userId based on view type and filter permission
      if (!isFilterShown && currentUser) {
        // If filters are NOT shown, force userId to current user (single value)
        params.userId = currentUser.id
      } else if (viewType === TaskViewType.ME && isFilterShown && currentUser && !activeFilter) {
        // For ME view with filters shown, default to current user ONLY if no filter is applied yet
        // Once filter is applied, we respect whatever the user selected (or removed)
        params.userId = [currentUser.id]
      }
      // For ALL, ALL_OPENED, COMPLETED views: userId should NOT be set initially

      // Add filter values if they exist
      if (activeFilter && isFilterShown) {
        // Task types
        if (activeFilter.taskTypesCode && activeFilter.taskTypesCode.length > 0) {
          params.taskTypesCode = activeFilter.taskTypesCode
        }

        // User ID - Use filter value if it exists, otherwise don't set userId
        // Note: TasksFilter.tsx removes userId from cleanPayload if empty array
        // So if activeFilter.userId exists, it means user explicitly selected someone
        if (activeFilter.userId && activeFilter.userId.length > 0) {
          params.userId = activeFilter.userId
        }
        // If userId is not in activeFilter (removed because empty), don't add it to params
        // This handles the case where user cleared the "Assigned to" dropdown

        // Workflow initiator
        if (activeFilter.workflowInitiatorId && activeFilter.workflowInitiatorId.length > 0) {
          params.workflowInitiatorId = activeFilter.workflowInitiatorId
        }

        // Workflow ID
        if (activeFilter.workflowId && Array.isArray(activeFilter.workflowId) && activeFilter.workflowId.length > 0) {
          params.workflowId = activeFilter.workflowId.map((item: any) => item.value)
        }

        // Date range
        if (activeFilter.startFrom) {
          const d = new Date(activeFilter.startFrom)
          d.setHours(0, 0, 0, 0)
          params.startFrom = d.getTime()
        }
        if (activeFilter.startTo) {
          const d = new Date(activeFilter.startTo)
          d.setHours(23, 59, 59, 0)
          params.startTo = d.getTime()
        }

        // EOS fields
        if (activeFilter.study) {
          params.studyFolderId = activeFilter.study
        }
        if (activeFilter.site) {
          params.siteFolderId = activeFilter.site
        }

        // ETMF fsiIds - Already computed by TasksFilter.tsx
        // The fsiIds array is built in handleApplyFilter() and passed via cleanPayload
        // Note: The 'data' field is removed by TasksFilter.tsx, so we use fsiIds directly
        if (activeFilter.fsiIds && activeFilter.fsiIds.length > 0) {
          params.fsiIds = activeFilter.fsiIds
        }
      }

      // Call the tasks API service
      const response = await api.tasks.getTasksList(params)

      return {
        elements: response.elements || [],
        totalElements: response.totalElements || 0
      }
    },
    [api, filterValues, viewType, isFilterShown, currentUser]
  )

  // Get title based on view type
  const getTitle = () => {
    switch (viewType) {
      case TaskViewType.ME:
        return t('Tasks')
      case TaskViewType.ALL:
        return t('ALL_TASKS')
      case TaskViewType.COMPLETED:
        return t('COMPLETED_TASKS')
      case TaskViewType.ALL_OPENED:
        return t('ALL_OPENED_TASKS')
      default:
        return t('TASKS_TITLE')
    }
  }

  // Handle filter changes - called when user clicks "Filter" button
  // Matches AngularJS behavior: filter data is passed and table refreshes
  const handleFilterChange = (newFilter: any) => {
    setFilterValues(newFilter)
    // Filter is now applied via tableContext.setFilter in TasksFilter component
    // No need to manually refresh here
  }

  // Store refresh function from wrapper (if needed for other purposes)
  const handleReloadController = useCallback((refreshFn: () => void) => {
    refreshTableRef.current = refreshFn
  }, [])

  return (
    <div className="mcc-content-frame theme-sandman">
      <div id="tasksPage" className="theme-sandman">
        <RcmTableDataWrapper
          key={viewType} // Force remount when view type changes
          loadData={loadData}
          persistenceKey="tasks"
          defaultSortBy="START_DATE"
          defaultSortDesc={true}
          initialPage={1}
          doPreload={!isFilterShown} // Only preload if no filter - filter will trigger load when ready
          reloadController={handleReloadController}
        >
          <div className="sub-page-header">
            <div className="d-flex flex-wrap">
              <div className="align-left d-flex">
                <h1 id="5a4294d8-2fc5-4c69-b8e4-a1dc66da8200">{t('Tasks')}</h1>
              </div>
              {isFilterShown && (
                <div className="align-right align-bottom d-flex">
                  <RcmTableFilterTrigger />
                </div>
              )}
            </div>
          </div>

          <div id="page-content">
            {isFilterShown && (
              <div id="tasks-filter">
                <TasksFilter viewType={viewType} onFilterChange={handleFilterChange} initialValues={filterValues} />
              </div>
            )}

            <TasksTable viewType={viewType} filterShown={isFilterShown} currentUserId={currentUser?.id} />
          </div>
        </RcmTableDataWrapper>
      </div>
      <Outlet />
    </div>
  )
}

export default Tasks
